import type { FfpDashboardData, MeasureKey, Month, MoneySplit } from './types'
import { monthRange } from './utils'
import type { GuideDirection, GuideStatus } from './utils'
import { COLOURS } from './components/charts/theme'

// The Health section's words and the small view helpers its cards and
// panels share. The design's approved copy for the three trend measures is
// also used by the section takeaway and the Early days cards.
type MeasureCopy = {
  direction: GuideDirection
  question: string
  // The panel's title and the Early days card's name.
  name: string
  // Card titles by utils.trendVerdict; steady gets "{neutral} since Feb 2026".
  title: string
  worse: string
  neutral: string
  bigLabel: string
  zoneLabel: (guide: number) => string
  // "60% of your people now pay under 10% of their pay towards debt"
  share: (share: number, guide: number) => string
  measures: string
  // What it is, without the guide: Early days cards have no guide to quote.
  about: string
  // "10% or less is comfortable."
  guideNote: (guide: number) => string
  definition: string
  // How the section takeaway names it when it is the one still behind.
  behind: string
  benchmark: (guide: number) => string
}

export const MEASURE_KEYS: MeasureKey[] = ['debt', 'spending', 'investing']

export const MEASURES: Record<MeasureKey, MeasureCopy> = {
  debt: {
    direction: 'at_or_under',
    question: 'What share of pay goes to debt repayments?',
    name: 'Debt repayments to gross income',
    title: 'Debt repayments are taking a smaller bite out of your people’s pay',
    worse: 'Debt repayments are taking a bigger bite out of your people’s pay',
    neutral: 'Debt repayments',
    bigLabel: 'of pay goes to debt repayments for a typical person',
    zoneLabel: (guide) => `${guide}% or less: comfortable`,
    share: (share, guide) => `${share}% of your people now pay ${guide}% or less of their pay towards debt`,
    measures: 'Debt repayments to gross income · average',
    about: 'How much of each person’s pay, before tax, goes to loans, credit cards and store accounts each month.',
    guideNote: (guide) => `${guide}% or less is comfortable.`,
    definition: 'The money a person pays each month towards loans, credit cards, store accounts, vehicle finance and their home loan, divided by their pay before tax.',
    behind: 'debt repayments are still above the healthy level',
    benchmark: (guide) => `The ${guide}% guide is Wealthbit’s financial planning guideline for a comfortable level of debt repayments.`,
  },
  spending: {
    direction: 'at_or_under',
    question: 'How much of their income is your team spending every month?',
    name: 'Total expenses to gross income',
    title: 'Your people are spending a smaller share of what they earn',
    worse: 'Your people are spending a bigger share of what they earn',
    neutral: 'Spending',
    bigLabel: 'of pay goes to monthly spending for a typical person',
    zoneLabel: (guide) => `${guide}% or less: room to save`,
    share: (share, guide) => `${share}% of your people now spend ${guide}% or less of their pay`,
    measures: 'Total expenses to gross income · average',
    about: 'Everything a person spends in a month, from rent to groceries, compared with their pay before tax.',
    guideNote: (guide) => `${guide}% or less leaves room to save and pay off debt.`,
    definition: 'What a person spends each month, including rent, transport and groceries, divided by their pay before tax. It also includes debt repayments and savings contributions, which have their own cards too.',
    behind: 'spending is still above the healthy level',
    benchmark: (guide) => `The ${guide}% guide is Wealthbit’s financial planning guideline for everyday spending.`,
  },
  investing: {
    direction: 'over',
    question: 'How much of their income is your team investing every month?',
    name: 'Total investment contributions to gross income',
    title: 'Your people are putting more of their pay towards the future',
    worse: 'Your people are putting less of their pay towards the future',
    neutral: 'Investing',
    bigLabel: 'of pay is invested each month by a typical person',
    zoneLabel: (guide) => `Over ${guide}%: building long-term security`,
    share: (share, guide) => `${share}% of your people now invest more than ${guide}% of their pay`,
    measures: 'Total investment contributions to gross income · average',
    about: 'What people put into retirement funds, unit trusts and savings accounts each month, compared with their pay before tax.',
    guideNote: (guide) => `More than ${guide}% builds long-term security.`,
    definition: 'Monthly contributions to retirement funds (including the company pension or provident fund), unit trusts, tax-free savings and fixed deposits, divided by pay before tax.',
    behind: 'investing is still short of the healthy level',
    benchmark: (guide) => `The ${guide}% guide is Wealthbit’s financial planning guideline for long-term saving, including retirement fund contributions.`,
  },
}

export const STATUS_LABELS: Record<GuideDirection, Record<GuideStatus, string>> = {
  at_or_under: { on_track: 'On track: within the guide', watch: 'Watch: just above the guide', needs_attention: 'Needs attention' },
  over: { on_track: 'On track: over the guide', watch: 'Watch: just below the guide', needs_attention: 'Needs attention' },
}

export const AVERAGE_CALC =
  'We work out each person’s share of pay before tax, then average those shares. A few very high shares can pull the average up, so the card also shows how many people are within the guide.'

// Lowest band first, matching health.retirement_bands.bands.
export const BAND_NAMES = ['Highly unlikely', 'Unlikely', 'Possibly', 'Likely', 'Highly likely']

export const RETIREMENT = {
  question: 'Is your team set up for a safe retirement?',
  name: 'Retirement readiness',
  title: 'More of your people are on course for a comfortable retirement',
  metric: 'Retirement readiness score · share of people in each band',
  meaning:
    'Retirement readiness asks: if your people keep saving the way they do now, how likely are they to have enough to live on when they stop working?',
}

export const ZERO_INCOME = {
  label: 'Excluded: income recorded as R0',
  detail: 'We can’t work out a share of pay without an income figure. These people still get coaching and will be counted once their payroll income comes through.',
}

export const FRESHNESS_SOURCE = 'Health measures: from the weekly capture of people’s financial snapshots'

// "12 excluded", or that nobody is.
export const excludedChip = (excluded: number): string => (excluded > 0 ? `${excluded} excluded` : 'Nobody excluded')

// "Harbour Logistics · Feb to Sep 2026 · n = 156 of 168 people"
export const cardFooter = (data: FfpDashboardData, months: { month: Month }[], people: number): string =>
  footerLine(data, months, `${people} of ${data.people.onboarded} people`)

// "Harbour Logistics · Feb to Sep 2026 · n = 240 enrolled": the company, the
// months shown and who is counted.
export const footerLine = (data: FfpDashboardData, months: { month: Month }[], counted: string): string => {
  const parts = [data.organization.name]
  if (months.length > 0) parts.push(monthRange(months[0].month, months[months.length - 1].month))
  parts.push(`n = ${counted}`)
  return parts.join(' · ')
}

type PartKey = 'expenses' | 'debt' | 'investments' | 'remaining'

// Debt is dark green, never orange: orange means "needs attention" on this page.
export const MONEY_PARTS: { key: PartKey; name: string; sub: string; colour: string; text: string }[] = [
  { key: 'expenses', name: 'Everyday spending', sub: 'Rent, food, transport', colour: COLOURS.forestFill, text: COLOURS.ink },
  { key: 'debt', name: 'Debt', sub: 'Loans and cards', colour: COLOURS.forestDarker, text: COLOURS.white },
  { key: 'investments', name: 'Investing', sub: 'Retirement and savings', colour: COLOURS.forestLight, text: COLOURS.white },
  { key: 'remaining', name: 'Everything else', sub: 'Tax, deductions and what’s left', colour: COLOURS.grey200, text: COLOURS.ink },
]

// The first month and the latest, or just the latest when they're the same.
export const moneySplits = (first: MoneySplit | null, latest: MoneySplit | null): MoneySplit[] => {
  if (!latest) return []
  if (!first || first.month === latest.month) return [latest]
  return [first, latest]
}

